import * as React from 'react'
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { documentService } from '@/services/DocumentService'
import { clientKeys, documentKeys, proposalKeys } from '@/hooks/queryKeys'
import type { DocumentDto } from '@lib/dto/document.dto'
import type { UploadFileMeta } from '@/types/DocumentService.types'

/**
 * Document write operations.
 *
 * Every one invalidates documents, clients, AND proposals. That looks broad,
 * but a document shows up in all three places — a site's map list, the client
 * detail it belongs to, and any proposal version it is attached to — so a
 * narrower invalidation just means one of those views goes stale.
 */
function useInvalidateDocuments(): () => void {
  const queryClient = useQueryClient()
  return React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: documentKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: clientKeys.all })
    void queryClient.invalidateQueries({ queryKey: proposalKeys.all })
  }, [queryClient])
}

export interface UploadDocumentVariables {
  file: File
  meta: UploadFileMeta
}

/**
 * Upload a file: reserve, PUT to storage, confirm. Resolves only once the API
 * has confirmed the object landed, so a caller that awaits this knows the
 * document is genuinely usable rather than merely reserved.
 */
export function useUploadDocument(): UseMutationResult<
  DocumentDto,
  Error,
  UploadDocumentVariables
> {
  const invalidate = useInvalidateDocuments()
  return useMutation({
    mutationFn: ({ file, meta }: UploadDocumentVariables) =>
      documentService.uploadFile(file, meta),
    onSuccess: () => invalidate(),
  })
}

export interface ArchiveDocumentVariables {
  id: string
  archived: boolean
}

export function useArchiveDocument(): UseMutationResult<
  DocumentDto,
  Error,
  ArchiveDocumentVariables
> {
  const invalidate = useInvalidateDocuments()
  return useMutation({
    mutationFn: ({ id, archived }: ArchiveDocumentVariables) =>
      documentService.setArchived(id, archived),
    onSuccess: () => invalidate(),
  })
}
